/**
 * How long a supervisor waits before trying an Environment again.
 *
 * Every retry against a Cloud Workspace is a request to AFAR Connect, and a steady trickle of them
 * keeps Connect's database from ever pausing. So retries back off to a ceiling a page left open
 * all night can afford, and the user coming back to the page (a wakeup) restarts the ladder, so
 * someone watching never waits on the ceiling.
 */
const RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000] as const;
const RETRY_CEILING_MS = 60_000;
/** Failures in a row after which retries fall to `RETRY_IDLE_MS`: about ten minutes of trying. */
const RETRY_PERSISTENT_FAILURES = 16;
/**
 * Retrying a host that has failed for ten minutes. Longer than Connect's database waits before it
 * pauses, so a page left open on a broken host stops keeping that database awake.
 */
const RETRY_IDLE_MS = 10 * 60_000;

/** Retrying a host that is still starting, while a normal start could still be under way. */
export const ENVIRONMENT_STARTING_RETRY_MS = 2_000;
/** Longer than a Cloud Workspace takes to boot; waiting past it means the start is in trouble. */
export const ENVIRONMENT_STARTING_FAST_WINDOW_MS = 3 * 60_000;
/** How often a start that outlived the fast window is checked on. */
export const ENVIRONMENT_STARTING_SLOW_RETRY_MS = 30_000;
/**
 * Longer than any provider takes to start a Cloud Workspace; a start still waited on past it is
 * checked on only every `RETRY_IDLE_MS`, until the user comes back.
 */
export const ENVIRONMENT_STARTING_SLOW_WINDOW_MS = 20 * 60_000;

/** The backoff before the next attempt after `failureCount` failures in a row (1 or more). */
export function failureRetryDelayMs(failureCount: number): number {
  if (failureCount > RETRY_PERSISTENT_FAILURES) return RETRY_IDLE_MS;
  return RETRY_DELAYS_MS[Math.max(failureCount - 1, 0)] ?? RETRY_CEILING_MS;
}

/**
 * Waiting on a starting host does not back off at first: a run ready at 50s would otherwise be
 * seen at the 63s retry. A start that has outlived any normal boot is checked on far less often.
 */
export function startingRetryDelayMs(waitingMs: number): number {
  if (waitingMs < ENVIRONMENT_STARTING_FAST_WINDOW_MS) return ENVIRONMENT_STARTING_RETRY_MS;
  if (waitingMs < ENVIRONMENT_STARTING_SLOW_WINDOW_MS) return ENVIRONMENT_STARTING_SLOW_RETRY_MS;
  return RETRY_IDLE_MS;
}
